import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import SidePanel from "components/sidePanel";
import UploadFile from "components/uploadFile";
import { ReactComponent as AttachIcon } from "images/auth/attach.svg";
import { fileToBuffer } from "utils/fileToBuffer";
import uploadApi from "api/uploadFile";
import useNotify from "hooks/useNotify";
import FileIcon from "utils/iconHelper";
import { deliverablesType, nonMimeTypeFiles } from "utils/constantValues";
import { actions } from "store/actions";
import { useTranslation } from "react-i18next";

function DeliverablesPanel({ isOpen, onClose, activeAssignee, projectId }) {
  const inputRef = useRef(null);

  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.userDetails || {});

  const [isUploading, setIsUploading] = useState(false);
  const [localFile, setLocalFile] = useState(null);
  const { notifyError, notifySuccess } = useNotify();

  const uploadHandler = async (e, isDrag) => {
    e.preventDefault();
    const fileList = isDrag ? [...e.dataTransfer.files] : [...e.target.files];
    const file = fileList?.[0];

    if (!file) return;

    try {
      setIsUploading(true);
      const name = file.name ?? "";
      const extension = name.substr(name.lastIndexOf(".") + 1).toLowerCase();
      const mimeType = file?.type ?? nonMimeTypeFiles[extension] ?? "";

      const { id, url } = await uploadApi.getFileUrlApi({
        name,
        type: deliverablesType,
        mimeType,
      });

      const buffer = await fileToBuffer(file);
      await axios.put(url, buffer, { headers: { "Content-Type": mimeType } });

      const uploaded = { fileId: id };

      dispatch(
        actions.uploadDeliverable(
          activeAssignee?.assigneeId,
          uploaded,
          (isSuccess, err) => {
            setIsUploading(false);
            if (isSuccess) {
              notifySuccess("Uploaded successfully!");
              setLocalFile((prev) => ({ ...prev, fileName: file.name }));

              if (projectId) {
                dispatch(
                  actions.getAssigneesPerProject(
                    userDetails?.agencyId,
                    projectId,
                    () => {}
                  )
                );
              } else {
                dispatch(
                  actions.getVendorTasks(
                    userDetails?.vendorId,
                    {},
                    (success, err) => {}
                  )
                );
              }
            } else {
              notifyError(
                err?.response?.data?.message ?? err?.message ?? "Upload failed!"
              );
            }
          }
        )
      );
    } catch (err) {
      console.error(err);
      notifyError(
        err?.response?.data?.message ??
          err?.data?.message ??
          err.message ??
          "Upload failed!"
      );
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setLocalFile(activeAssignee?.deliverables);
    } else {
      setLocalFile(null);
    }
  }, [activeAssignee, isOpen]);

  const [t] = useTranslation();

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={t("deliverablesTitle")}
      width={354}
    >
      <div className="p-[24px] flex flex-col gap-[16px]">
        <UploadFile
          name="deliverables"
          register={() => ({})}
          label={t("uploadFile")}
          labelClassName="text-[#1E4A6B] text-[16px] not-italic font-medium"
          isRequired={true}
          isMultiFile={false}
          fileName={localFile?.fileName}
          placeHolderLabel={t("attachFilesPlaceholder")}
          placeHolderContainerClassName="flex p-[14px] justify-between items-center gap-[8px] self-stretch rounded-[6px] h-[54px] w-full border-[1px] border-dashed border-dark-gray cursor-pointer"
          icon={<AttachIcon />}
          isUploading={isUploading}
          getFile={uploadHandler}
          ref={inputRef}
          showDeleteIcon={false}
        />

        {/* Single file display (no delete) */}
        {localFile && (
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-[5px] text-[#1D1B22]">
              <FileIcon fileName={localFile.fileName} />
              <span
                className="text[16px] font-medium text-[#1D1B22] truncate max-w-[300px]"
                title={localFile.fileName}
              >
                {localFile.fileName}
              </span>
            </div>
          </div>
        )}
      </div>
    </SidePanel>
  );
}

DeliverablesPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  files: PropTypes.array,
  onUpload: PropTypes.func,
};

export default DeliverablesPanel;
